import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ActionButtons, ConfirmModal, useToast } from "../../../shared/components/SharedUI";
import {
  getAreas, createArea, updateArea, deleteArea,
  createHorario, updateHorario, deleteHorario,
  getCuentasIngreso,
} from "../services/portalService";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useBreakpoint() {
  const get = () => window.innerWidth < 640 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop";
  const [bp, setBp] = useState(get);
  useEffect(() => {
    const fn = () => setBp(get());
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return bp;
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color = "#f97316" }) {
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-card)",
      borderRadius: 12, padding: "16px 20px", display: "flex",
      alignItems: "center", gap: 14, flex: "1 1 140px", minWidth: 0,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: `${color}18`, border: `1px solid ${color}30`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color, fontSize: 16,
      }}>
        <i className={`fa-solid ${icon}`} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "var(--text-primary)", lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 3, fontWeight: 500, whiteSpace: "nowrap" }}>{label}</div>
      </div>
    </div>
  );
}

// ── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ icon, children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: "#f97316",
      textTransform: "uppercase", letterSpacing: 1, marginBottom: 12,
      display: "flex", alignItems: "center", gap: 6,
    }}>
      <i className={`fa-solid ${icon}`} style={{ fontSize: 10 }} />
      {children}
    </div>
  );
}

export default function AdminAreas() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();
  const bp = useBreakpoint();
  const isMobile = bp === "mobile";
  const imgRef = useRef();

  const [areas,         setAreas]         = useState([]);
  const [cuentasIngreso, setCuentas]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [modal,         setModal]         = useState(null);
  const [form,          setForm]          = useState({});
  const [imageFile,     setImageFile]     = useState(null);
  const [imagePreview,  setImagePreview]  = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [err,           setErr]           = useState(null);
  const [horarioModal,  setHModal]        = useState(null);
  const [hForm,         setHForm]         = useState({});
  const [selectedArea,  setSelArea]       = useState(null);
  const [confirm,       setConfirm]       = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [expandedId,    setExpandedId]    = useState(null);
  const [search,        setSearch]        = useState("");

  function load() {
    getAreas().then(d => setAreas(d.results || d)).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    getCuentasIngreso().then(setCuentas).catch(() => {});
  }, []);

  function openCreate() {
    setForm({ nombre: "", descripcion: "", activa: true, precio_colones: "", precio_dolares: "", cuenta_ingreso: "" });
    setImageFile(null); setImagePreview(null); setErr(null); setModal({ mode: "create" });
  }
  function openEdit(a) {
    setForm({
      nombre: a.nombre, descripcion: a.descripcion, activa: a.activa,
      precio_colones: a.precio_colones ?? "", precio_dolares: a.precio_dolares ?? "",
      cuenta_ingreso: a.cuenta_ingreso_id ?? "",
    });
    setImageFile(null); setImagePreview(a.imagen_url || null); setErr(null); setModal({ mode: "edit", id: a.id });
  }

  function handleImageChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  async function handleSaveArea() {
    setSaving(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("nombre", form.nombre);
      fd.append("descripcion", form.descripcion);
      fd.append("activa", form.activa ? "true" : "false");
      if (form.precio_colones !== "" && form.precio_colones !== null) fd.append("precio_colones", form.precio_colones);
      if (form.precio_dolares !== "" && form.precio_dolares !== null) fd.append("precio_dolares", form.precio_dolares);
      if (form.cuenta_ingreso !== "" && form.cuenta_ingreso !== null) fd.append("cuenta_ingreso", form.cuenta_ingreso);
      else fd.append("cuenta_ingreso", "");
      if (imageFile) fd.append("imagen", imageFile);
      if (modal.mode === "create") await createArea(fd);
      else await updateArea(modal.id, fd);
      showToast(t("common.guardado"), true);
      setModal(null); load();
    } catch (e) {
      try { const d = JSON.parse(e.message); setErr(Object.values(d).flat().join(". ")); }
      catch { setErr(e.message); }
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      if (confirm.type === "area") await deleteArea(confirm.id);
      else await deleteHorario(confirm.id);
      showToast(t("common.eliminado"), true);
      setConfirm(null); load();
    } catch { showToast(t("common.errorEliminar"), false); }
    finally { setDeleting(false); }
  }

  function openNewHorario(area) {
    setSelArea(area);
    setHForm({ nombre: "", hora_inicio: "", hora_fin: "", activo: true });
    setErr(null); setHModal({ mode: "create" });
  }
  function openEditHorario(area, h) {
    setSelArea(area);
    setHForm({ nombre: h.nombre, hora_inicio: h.hora_inicio, hora_fin: h.hora_fin, activo: h.activo });
    setErr(null); setHModal({ mode: "edit", id: h.id });
  }
  async function handleSaveHorario() {
    setSaving(true); setErr(null);
    try {
      if (horarioModal.mode === "create") await createHorario({ ...hForm, area: selectedArea.id });
      else await updateHorario(horarioModal.id, hForm);
      showToast(t("common.guardado"), true);
      setHModal(null); load();
    } catch (e) {
      try { const d = JSON.parse(e.message); setErr(Object.values(d).flat().join(". ")); }
      catch { setErr(e.message); }
    } finally { setSaving(false); }
  }

  // Stats
  const totalActivas   = areas.filter(a => a.activa).length;
  const totalInactivas = areas.filter(a => !a.activa).length;

  const filtered = search
    ? areas.filter(a => a.nombre.toLowerCase().includes(search.toLowerCase()))
    : areas;

  const fldStyle = {
    width: "100%", boxSizing: "border-box",
    padding: "9px 12px", borderRadius: 8,
    border: "1px solid var(--border-card)",
    background: "var(--bg-input, var(--hover-bg))",
    color: "var(--text-primary)", fontSize: 13, outline: "none",
  };
  const labelSt = {
    fontSize: 11, fontWeight: 700, color: "var(--text-secondary)",
    display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5,
  };

  const ModalOverlay = ({ onClose, children, wide }) => (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      backdropFilter: "blur(4px)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 16, width: `min(${wide || 540}px, 96vw)`,
        boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
        maxHeight: "92vh", overflowY: "auto",
      }}>
        {children}
      </div>
    </div>
  );

  const ModalHeader = ({ icon, title, subtitle, color = "#f97316", onClose }) => (
    <div style={{
      padding: "22px 24px 18px", borderBottom: "1px solid var(--border-card)",
      display: "flex", alignItems: "center", justifyContent: "space-between",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: `${color}18`, border: `1px solid ${color}30`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color, fontSize: 16,
        }}>
          <i className={`fa-solid ${icon}`} />
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>{subtitle}</div>}
        </div>
      </div>
      <button onClick={onClose} style={{
        background: "none", border: "none", color: "var(--text-muted)",
        cursor: "pointer", fontSize: 18, padding: 4,
      }}>
        <i className="fa-solid fa-xmark" />
      </button>
    </div>
  );

  const ModalFooter = ({ onCancel, onSave, saveLabel, saving: sv }) => (
    <div style={{
      padding: "16px 24px 20px", borderTop: "1px solid var(--border-card)",
      display: "flex", gap: 10, justifyContent: "flex-end",
    }}>
      <button onClick={onCancel} style={{
        padding: "9px 20px", borderRadius: 8, border: "1px solid var(--border-card)",
        background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontSize: 14,
      }}>
        {t("common.cancelar")}
      </button>
      <button onClick={onSave} disabled={sv} style={{
        padding: "9px 22px", borderRadius: 8, border: "none",
        background: sv ? "#b45309" : "#f97316", color: "#fff",
        fontWeight: 700, fontSize: 14, cursor: sv ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        {sv
          ? <><i className="fa-solid fa-circle-notch fa-spin" /> Guardando…</>
          : <><i className="fa-solid fa-floppy-disk" /> {saveLabel || t("common.guardar")}</>}
      </button>
    </div>
  );

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      {toastEl}
      <ConfirmModal open={!!confirm} name={confirm?.name} onConfirm={handleDelete} onCancel={() => setConfirm(null)} loading={deleting} />

      {/* Breadcrumb */}
      <div style={{ marginBottom: 6 }}>
        <Link to="/" style={{ textDecoration: "none", color: "var(--text-secondary)", fontSize: 13 }}>
          Portal Admin
        </Link>
        <span style={{ color: "var(--text-secondary)", fontSize: 13 }}> / </span>
        <span style={{ color: "var(--text-primary)", fontWeight: 600, fontSize: 13 }}>
          {t("portal.areasComunes")}
        </span>
      </div>

      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 22,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 24, fontWeight: 800, color: "var(--text-primary)" }}>
            {t("portal.areasComunes")}
          </h2>
          <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: 13 }}>
            {t("portal.areasComunesDesc")}
          </p>
        </div>
        <button onClick={openCreate} style={{
          background: "#f97316", color: "#fff", border: "none", borderRadius: 10,
          padding: "10px 18px", cursor: "pointer", fontWeight: 700, fontSize: 13,
          display: "flex", alignItems: "center", gap: 7,
          boxShadow: "0 2px 8px rgba(249,115,22,0.35)",
        }}>
          <i className="fa-solid fa-plus" />
          {isMobile ? "Nueva" : t("portal.nueva")}
        </button>
      </div>

      {/* Stats */}
      {!loading && areas.length > 0 && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 22 }}>
          <StatCard icon="fa-building"      label="Total áreas"    value={areas.length}   color="#f97316" />
          <StatCard icon="fa-circle-check"  label="Activas"        value={totalActivas}   color="#10b981" />
          <StatCard icon="fa-circle-xmark"  label="Inactivas"      value={totalInactivas} color="#6b7280" />
          <StatCard icon="fa-clock"         label="Total horarios" value={areas.reduce((s, a) => s + (a.horarios?.length || 0), 0)} color="#3b82f6" />
        </div>
      )}

      {/* Search bar */}
      {!loading && areas.length > 0 && (
        <div style={{
          background: "var(--bg-card)", border: "1px solid var(--border-card)",
          borderRadius: 12, padding: "12px 14px", marginBottom: 18,
          display: "flex", gap: 8, alignItems: "center",
        }}>
          <div style={{ position: "relative", flex: 1 }}>
            <i className="fa-solid fa-magnifying-glass" style={{
              position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
              color: "var(--text-secondary)", fontSize: 12, pointerEvents: "none",
            }} />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar área por nombre…"
              style={{
                padding: "8px 12px 8px 30px", borderRadius: 8, width: "100%", boxSizing: "border-box",
                border: "1px solid var(--border-card)", background: "var(--hover-bg)",
                color: "var(--text-primary)", fontSize: 13, outline: "none",
              }}
            />
          </div>
          <span style={{
            fontSize: 12, color: "var(--text-secondary)", background: "var(--hover-bg)",
            borderRadius: 20, padding: "4px 10px", fontWeight: 600, whiteSpace: "nowrap",
          }}>
            {filtered.length} área{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div style={{ padding: 48, textAlign: "center", color: "var(--text-secondary)" }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: 24, marginBottom: 12, display: "block" }} />
          Cargando áreas…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          background: "var(--bg-card)", border: "1px solid var(--border-card)",
          borderRadius: 14, padding: "52px 24px", textAlign: "center",
        }}>
          <div style={{
            width: 60, height: 60, borderRadius: "50%",
            background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 16px", color: "#f97316", fontSize: 24,
          }}>
            <i className="fa-solid fa-building" />
          </div>
          <p style={{ margin: 0, fontWeight: 700, color: "var(--text-primary)", fontSize: 15 }}>
            {areas.length === 0 ? t("portal.sinAreas") : "Sin resultados"}
          </p>
          <p style={{ margin: "6px 0 0", color: "var(--text-secondary)", fontSize: 13 }}>
            {areas.length === 0
              ? "Creá la primera área común para los residentes."
              : "Probá con otro nombre de búsqueda."}
          </p>
          {areas.length === 0 && (
            <button onClick={openCreate} style={{
              marginTop: 16, background: "#f97316", color: "#fff", border: "none",
              borderRadius: 8, padding: "9px 18px", cursor: "pointer", fontWeight: 700, fontSize: 13,
            }}>
              <i className="fa-solid fa-plus" style={{ marginRight: 6 }} /> Crear primera área
            </button>
          )}
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 14,
        }}>
          {filtered.map(a => {
            const isExpanded = expandedId === a.id;
            return (
              <div key={a.id} style={{
                background: "var(--bg-card)", border: "1px solid var(--border-card)",
                borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column",
                transition: "box-shadow 0.2s",
              }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.15)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
              >
                {/* Image */}
                {a.imagen_url ? (
                  <img src={a.imagen_url} alt={a.nombre}
                    style={{ width: "100%", height: 110, objectFit: "cover", display: "block", flexShrink: 0 }} />
                ) : (
                  <div style={{
                    width: "100%", height: 70, flexShrink: 0,
                    background: "rgba(249,115,22,0.06)", borderBottom: "1px solid var(--border-card)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <i className="fa-solid fa-building" style={{ fontSize: 28, color: "#f97316", opacity: 0.4 }} />
                  </div>
                )}

                {/* Body */}
                <div style={{ padding: "14px 16px", flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {a.nombre}
                      </p>
                      {a.descripcion && (
                        <p style={{
                          margin: "3px 0 0", fontSize: 12, color: "var(--text-secondary)",
                          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                        }}>
                          {a.descripcion}
                        </p>
                      )}
                    </div>
                    <ActionButtons
                      onEdit={() => openEdit(a)}
                      onDelete={() => setConfirm({ id: a.id, name: a.nombre, type: "area" })}
                    />
                  </div>

                  {/* Tags row */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{
                      background: a.activa ? "rgba(16,185,129,0.1)" : "rgba(107,114,128,0.1)",
                      border: `1px solid ${a.activa ? "rgba(16,185,129,0.3)" : "rgba(107,114,128,0.3)"}`,
                      color: a.activa ? "#10b981" : "#6b7280",
                      borderRadius: 20, padding: "2px 9px", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap",
                    }}>
                      <i className={`fa-solid ${a.activa ? "fa-circle-check" : "fa-circle-xmark"}`} style={{ marginRight: 4, fontSize: 9 }} />
                      {a.activa ? t("portal.activa") : t("portal.inactiva")}
                    </span>
                    <span style={{ fontSize: 11, color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                      <i className="fa-solid fa-clock" style={{ marginRight: 4, opacity: 0.6 }} />
                      {a.horarios?.length || 0} horario{(a.horarios?.length || 0) !== 1 ? "s" : ""}
                    </span>
                    {(a.precio_colones || a.precio_dolares) && (
                      <div style={{ display: "flex", gap: 6 }}>
                        {a.precio_colones && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#10b981" }}>
                            ₡{Number(a.precio_colones).toLocaleString("en-US")}
                          </span>
                        )}
                        {a.precio_dolares && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#3b82f6" }}>
                            ${Number(a.precio_dolares).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Horarios toggle */}
                  <button onClick={() => setExpandedId(isExpanded ? null : a.id)} style={{
                    background: "none", border: "1px solid var(--border-card)", borderRadius: 8,
                    padding: "6px 12px", cursor: "pointer", fontSize: 12,
                    color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 6,
                    width: "100%", justifyContent: "center", marginTop: "auto",
                    transition: "background 0.12s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--hover-bg)"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}
                  >
                    <i className={`fa-solid fa-chevron-${isExpanded ? "up" : "down"}`} style={{ fontSize: 10 }} />
                    {isExpanded ? "Ocultar horarios" : "Ver horarios"}
                  </button>

                  {/* Horarios panel */}
                  {isExpanded && (
                    <div style={{ borderTop: "1px solid var(--border-card)", paddingTop: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 1 }}>
                          {t("portal.horarios")}
                        </span>
                        <button onClick={() => openNewHorario(a)} style={{
                          background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.3)",
                          color: "#f97316", borderRadius: 6, padding: "3px 8px",
                          cursor: "pointer", fontSize: 11, fontWeight: 700,
                        }}>
                          + Agregar
                        </button>
                      </div>
                      {!a.horarios?.length ? (
                        <p style={{ color: "var(--text-secondary)", fontSize: 12, margin: 0 }}>{t("portal.sinHorarios")}</p>
                      ) : (
                        a.horarios.map(h => (
                          <div key={h.id} style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            padding: "7px 0", borderBottom: "1px solid var(--border-card)",
                          }}>
                            <div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: h.activo ? "var(--text-primary)" : "var(--text-secondary)" }}>
                                {h.nombre}
                              </span>
                              <span style={{ fontSize: 11, color: "var(--text-secondary)", marginLeft: 6 }}>
                                {h.hora_inicio} – {h.hora_fin}
                              </span>
                              {!h.activo && (
                                <span style={{
                                  marginLeft: 6, fontSize: 10, background: "rgba(107,114,128,0.1)",
                                  border: "1px solid rgba(107,114,128,0.25)", color: "#9ca3af",
                                  borderRadius: 10, padding: "1px 6px",
                                }}>
                                  inactivo
                                </span>
                              )}
                            </div>
                            <ActionButtons
                              onEdit={() => openEditHorario(a, h)}
                              onDelete={() => setConfirm({ id: h.id, name: h.nombre, type: "horario" })}
                            />
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Area Modal ── */}
      {modal && (
        <ModalOverlay onClose={() => setModal(null)} wide={560}>
          <ModalHeader
            icon={modal.mode === "create" ? "fa-plus" : "fa-pen"}
            title={modal.mode === "create" ? t("portal.crearArea") : t("portal.editarArea")}
            subtitle={modal.mode === "create" ? "Configurá la nueva área común" : "Modificá los datos del área"}
            onClose={() => setModal(null)}
          />
          <div style={{ padding: "22px 24px", display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Imagen */}
            <div>
              <SectionLabel icon="fa-image">Imagen del área</SectionLabel>
              <div onClick={() => imgRef.current?.click()} style={{
                borderRadius: 10, overflow: "hidden", cursor: "pointer",
                border: `2px dashed ${imagePreview ? "#f97316" : "var(--border-card)"}`,
                background: imagePreview ? "transparent" : "var(--hover-bg)",
                minHeight: 130, position: "relative",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {imagePreview ? (
                  <>
                    <img src={imagePreview} alt="preview"
                      style={{ width: "100%", height: 170, objectFit: "cover", display: "block" }} />
                    <div style={{
                      position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      opacity: 0, transition: "opacity .15s",
                    }}
                      onMouseEnter={e => e.currentTarget.style.opacity = 1}
                      onMouseLeave={e => e.currentTarget.style.opacity = 0}
                    >
                      <span style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>
                        <i className="fa-solid fa-camera" style={{ marginRight: 6 }} />
                        {t("portal.cambiarImagen")}
                      </span>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: 20 }}>
                    <i className="fa-solid fa-image" style={{ fontSize: 28, color: "var(--text-secondary)", opacity: 0.4, display: "block", marginBottom: 8 }} />
                    <p style={{ margin: 0, fontSize: 13, color: "var(--text-secondary)" }}>{t("portal.subirImagen")}</p>
                    <p style={{ margin: "3px 0 0", fontSize: 11, color: "var(--text-secondary)", opacity: 0.6 }}>JPG, PNG, WebP</p>
                  </div>
                )}
              </div>
              <input ref={imgRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageChange} />
              {imagePreview && (
                <button type="button" onClick={() => { setImageFile(null); setImagePreview(null); if (imgRef.current) imgRef.current.value = ""; }} style={{
                  marginTop: 6, background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)",
                  color: "#f87171", borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600,
                }}>
                  <i className="fa-solid fa-trash" style={{ marginRight: 4 }} />{t("common.limpiar")}
                </button>
              )}
            </div>

            {/* Info */}
            <div>
              <SectionLabel icon="fa-circle-info">Información</SectionLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label style={labelSt}>{t("portal.nombre")} <span style={{ color: "#f87171" }}>*</span></label>
                  <input value={form.nombre ?? ""} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                    style={fldStyle} placeholder="Ej: Piscina, Salón comunal…" />
                </div>
                <div>
                  <label style={labelSt}>{t("portal.descripcion")}</label>
                  <textarea value={form.descripcion ?? ""} rows={3}
                    onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                    style={{ ...fldStyle, resize: "vertical" }} />
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!form.activa}
                    onChange={e => setForm(f => ({ ...f, activa: e.target.checked }))}
                    style={{ width: 16, height: 16, accentColor: "#f97316" }} />
                  <span style={{ fontSize: 13, color: "var(--text-primary)", fontWeight: 500 }}>{t("portal.activa")}</span>
                </label>
              </div>
            </div>

            {/* Precios */}
            <div>
              <SectionLabel icon="fa-tag">Precios de reservación</SectionLabel>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelSt}>Precio (₡)</label>
                  <input type="number" min="0" step="0.01" value={form.precio_colones ?? ""}
                    onChange={e => setForm(f => ({ ...f, precio_colones: e.target.value }))}
                    style={fldStyle} placeholder="0.00" />
                </div>
                <div>
                  <label style={labelSt}>Precio ($)</label>
                  <input type="number" min="0" step="0.01" value={form.precio_dolares ?? ""}
                    onChange={e => setForm(f => ({ ...f, precio_dolares: e.target.value }))}
                    style={fldStyle} placeholder="0.00" />
                </div>
              </div>
            </div>

            {/* Cuenta */}
            <div>
              <SectionLabel icon="fa-book">Contabilidad</SectionLabel>
              <label style={labelSt}>{t("portal.cuentaIngreso") || "Cuenta de ingreso"}</label>
              <select value={form.cuenta_ingreso ?? ""} onChange={e => setForm(f => ({ ...f, cuenta_ingreso: e.target.value }))} style={fldStyle}>
                <option value="">— Sin cuenta —</option>
                {cuentasIngreso.map(c => (
                  <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                ))}
              </select>
            </div>

            {err && (
              <div style={{
                background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
                borderLeft: "3px solid #f87171", borderRadius: 8,
                padding: "10px 14px", color: "#f87171", fontSize: 13,
                display: "flex", gap: 8,
              }}>
                <i className="fa-solid fa-circle-exclamation" style={{ flexShrink: 0, marginTop: 1 }} />
                {err}
              </div>
            )}
          </div>
          <ModalFooter onCancel={() => setModal(null)} onSave={handleSaveArea} saving={saving} />
        </ModalOverlay>
      )}

      {/* ── Horario Modal ── */}
      {horarioModal && (
        <ModalOverlay onClose={() => setHModal(null)} wide={460}>
          <ModalHeader
            icon={horarioModal.mode === "create" ? "fa-clock" : "fa-pen"}
            title={horarioModal.mode === "create" ? t("portal.agregarHorario") : t("portal.editarHorario")}
            subtitle={selectedArea?.nombre}
            onClose={() => setHModal(null)}
          />
          <div style={{ padding: "22px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={labelSt}>{t("portal.nombreHorario")} <span style={{ color: "#f87171" }}>*</span></label>
              <input value={hForm.nombre ?? ""} onChange={e => setHForm(f => ({ ...f, nombre: e.target.value }))}
                style={fldStyle} placeholder="Ej: Mañana, Tarde, Noche" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelSt}>{t("portal.horaInicio")}</label>
                <input type="time" value={hForm.hora_inicio ?? ""} onChange={e => setHForm(f => ({ ...f, hora_inicio: e.target.value }))} style={fldStyle} />
              </div>
              <div>
                <label style={labelSt}>{t("portal.horaFin")}</label>
                <input type="time" value={hForm.hora_fin ?? ""} onChange={e => setHForm(f => ({ ...f, hora_fin: e.target.value }))} style={fldStyle} />
              </div>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={!!hForm.activo}
                onChange={e => setHForm(f => ({ ...f, activo: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: "#f97316" }} />
              <span style={{ fontSize: 13, color: "var(--text-primary)", fontWeight: 500 }}>{t("portal.activo")}</span>
            </label>
            {err && (
              <div style={{
                background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
                borderLeft: "3px solid #f87171", borderRadius: 8,
                padding: "10px 14px", color: "#f87171", fontSize: 13,
              }}>
                {err}
              </div>
            )}
          </div>
          <ModalFooter onCancel={() => setHModal(null)} onSave={handleSaveHorario} saving={saving} />
        </ModalOverlay>
      )}
    </div>
  );
}
